// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module TopPagesTable
 * @package @geenius/analytics/react
 * @description Renders the React top-pages table used by analytics dashboards
 * to compare the most-visited routes. The component includes loading and empty
 * states so it can be mounted directly in page layouts.
 */

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Progress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@geenius/ui/react";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";

/** Summary row rendered by the React top-pages table. */
export interface TopPageSummary {
  url: string;
  views: number;
  uniqueVisitors?: number;
  averageTimeMs?: number;
  averageTimeSeconds?: number;
  averageTime?: string;
}

function labelProgressBar(node: HTMLDivElement | null, label: string): void {
  node
    ?.querySelector('[role="progressbar"]')
    ?.setAttribute("aria-label", label);
}

function normalizeCount(value: number | undefined): number | undefined {
  return Number.isFinite(value) ? Math.max(0, value ?? 0) : undefined;
}

function formatDuration(page: TopPageSummary): string {
  if (page.averageTime) return page.averageTime;

  const milliseconds =
    page.averageTimeMs ??
    (page.averageTimeSeconds !== undefined
      ? page.averageTimeSeconds * 1_000
      : undefined);
  if (!Number.isFinite(milliseconds)) return "-";

  const totalSeconds = Math.max(0, Math.round((milliseconds ?? 0) / 1_000));
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

function getErrorMessage(error: Error | string): string {
  return typeof error === "string" ? error : error.message;
}

/**
 * Props for rendering the React top-pages table.
 *
 * @property pages Ordered page summary rows with visit and engagement counts.
 * @property isLoading Whether the table should render placeholder rows.
 * @property error Optional table-level error message or object.
 * @property onRetry Optional retry handler rendered when an error is present.
 * @property className Optional class name merged into the table wrapper.
 */
export interface TopPagesTableProps {
  pages: TopPageSummary[];
  isLoading?: boolean;
  error?: Error | string | null;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders the React top-pages table with proportional view bars.
 *
 * @param props Table props containing page rows and optional loading state.
 * @returns A top-pages table element for analytics dashboards.
 */
export function TopPagesTable({
  pages,
  isLoading,
  error,
  onRetry,
  className,
}: TopPagesTableProps): React.ReactElement {
  const safePages = pages.map((page) => ({
    ...page,
    uniqueVisitors: normalizeCount(page.uniqueVisitors),
    views: Number.isFinite(page.views) ? Math.max(0, page.views) : 0,
  }));
  const maxViews =
    safePages.length > 0
      ? Math.max(...safePages.map((page) => page.views), 1)
      : 1;
  const copy = useAnalyticsCopy();

  if (error) {
    return (
      <Card className={className}>
        <CardHeader className="pb-0">
          <CardTitle className="text-sm" data-analytics-section-title>
            {copy.t("analytics.topPages.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid gap-3" role="alert">
            <span className="text-sm text-muted-foreground">
              {getErrorMessage(error)}
            </span>
            {onRetry && (
              <Button
                className="w-fit"
                type="button"
                variant="secondary"
                onClick={onRetry}
              >
                {copy.t("analytics.actions.retry")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
          <CardTitle className="text-sm">
            {copy.t("analytics.topPages.title")}
          </CardTitle>
          <span className="text-xs text-muted-foreground">
            {copy.t("analytics.topPages.loading")}
          </span>
        </CardHeader>
        <CardContent
          className="grid gap-3 pt-4"
          data-analytics-top-pages-loading
        >
          {[
            "skeleton-1",
            "skeleton-2",
            "skeleton-3",
            "skeleton-4",
            "skeleton-5",
          ].map((skeletonId) => (
            <Skeleton
              key={skeletonId}
              className="h-4 w-full"
              data-analytics-row-skeleton
            />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
        <CardTitle className="text-sm" data-analytics-section-title>
          {copy.t("analytics.topPages.title")}
        </CardTitle>
        <span className="text-xs text-muted-foreground">
          {copy.plural("analytics.topPages.routes", safePages.length)}
        </span>
      </CardHeader>
      <CardContent className="pt-4">
        {safePages.length === 0 ? (
          <EmptyState
            bordered={false}
            compact
            title={copy.t("analytics.topPages.empty")}
          />
        ) : (
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>
                  {copy.t("analytics.topPages.columns.page")}
                </TableHead>
                <TableHead>
                  {copy.t("analytics.topPages.columns.share")}
                </TableHead>
                <TableHead className="text-right">
                  {copy.t("analytics.topPages.columns.views")}
                </TableHead>
                <TableHead className="text-right">
                  {copy.t("analytics.dashboard.uniqueVisitors")}
                </TableHead>
                <TableHead className="text-right">
                  {copy.t("analytics.topPages.columns.averageTime")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {safePages.map((page) => (
                <TableRow key={page.url} data-analytics-page-row>
                  <TableCell className="max-w-0">
                    <span
                      className="block truncate text-sm font-medium text-foreground"
                      data-analytics-page-url
                    >
                      {page.url}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div
                      data-analytics-page-bar-container
                      ref={(node) =>
                        labelProgressBar(node, `${page.url} share`)
                      }
                    >
                      <Progress
                        className="w-full"
                        data-analytics-page-bar
                        value={page.views}
                        max={maxViews}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-xs font-semibold text-muted-foreground">
                    <span data-analytics-page-views>
                      {page.views.toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell className="text-right text-xs font-semibold text-muted-foreground">
                    <span data-analytics-page-unique-visitors>
                      {page.uniqueVisitors !== undefined
                        ? page.uniqueVisitors.toLocaleString()
                        : "-"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right text-xs font-semibold text-muted-foreground">
                    <span data-analytics-page-average-time>
                      {formatDuration(page)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
